import { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import ALL_SKILL_DEFS from "@/pages/Messaging/Integrations/allSkillDefs";
import type { SkillDef } from "@/pages/Messaging/Integrations/SkillControlsPanel";

/**
 * useSkillCatalog
 *
 * Fix for AI Skills Audit tracker item 12 (2026-09-12, velaro-messaging
 * docs/architecture/ai-skills-audit-remediation-2026-09-12.md).
 *
 * DESIGN: the velaro-messaging server (GET SiteSkillOverrides/catalog) is the sole
 * authority for the skill SET and the skill FACTS -- which skills exist right now,
 * their real Tier (-> write/destructive), subscriptionFlag, and whether this site's
 * actual subscription currently includes each one (`included`). allSkillDefs.ts is
 * decoration-only: its hand-written `label`/`group`/`description`/`channelWarning`
 * strings have no server-side home, so they're kept and merged on top by ToolName --
 * but they are NEVER trusted for facts a server field already answers.
 *
 * This closes two real bugs found by the audit:
 *
 * 1. 253 of 821 real backend IWorkflowSkill implementations (30.8%) were missing
 *    from allSkillDefs.ts entirely, so they never appeared in any admin skill
 *    picker. Because the server is now the SET authority, any skill it returns
 *    that has no allSkillDefs.ts entry still appears here -- using the server's own
 *    `label`/`category` as a fallback `label`/`group` and an empty `description` --
 *    instead of being silently dropped.
 *
 * 2. 33 skills where allSkillDefs.ts's hand-set `write`/`destructive` booleans
 *    actively DISAGREE with the live server Tier. Concrete example: `shopify_initiate_return`
 *    is hand-labeled `write: true` in allSkillDefs.ts, but the real server Tier is
 *    "Destructive" -- so a "Disable all destructive skills" click, or the write/
 *    destructive subscription gate, silently under-restricted it. `write` and
 *    `destructive` in the merged result below are ALWAYS derived from the server's
 *    `tier` field, never from allSkillDefs.ts.
 *
 * Conversely, an allSkillDefs.ts entry whose name the server does NOT return is
 * dropped from the merged result -- the server is the SET authority, so a removed
 * or renamed backend skill can't ghost-linger in the UI.
 */

export interface CatalogSkill extends SkillDef {
  /** Real server-computed tier -- the source of truth `write`/`destructive` above are derived from. */
  tier: "Read" | "Write" | "Destructive";
  subscriptionFlag: string | null;
  /** Server-computed: is this specific skill's SubscriptionFlag actually true on this
   *  site's real subscription right now. Never assume true from "integration connected". */
  included: boolean;
}

interface ServerCatalogEntry {
  name: string;
  label: string;
  category: string;
  tier: "Read" | "Write" | "Destructive";
  destructive: boolean;
  subscriptionFlag: string | null;
  included: boolean;
}

function mergeCatalog(serverEntries: ServerCatalogEntry[]): CatalogSkill[] {
  const staticByName = new Map<string, SkillDef>(
    ALL_SKILL_DEFS.map((s) => [s.name, s])
  );

  return serverEntries.map((entry) => {
    const decoration = staticByName.get(entry.name);
    return {
      name: entry.name,
      label: decoration?.label ?? entry.label,
      group: decoration?.group ?? entry.category,
      description: decoration?.description ?? "",
      channelWarning: decoration?.channelWarning,
      // ALWAYS derived from the server's real tier -- never allSkillDefs.ts's hand-set booleans.
      write: entry.tier === "Write",
      destructive: entry.tier === "Destructive",
      tier: entry.tier,
      subscriptionFlag: entry.subscriptionFlag,
      included: entry.included
    };
  });
}

async function fetchCatalog(
  api: ReturnType<typeof useApi>
): Promise<{ skills: CatalogSkill[]; error: boolean }> {
  try {
    const resp = await api.messaging.get("SiteSkillOverrides/catalog");
    if (!resp.ok) {
      return { skills: [], error: true };
    }
    const data: ServerCatalogEntry[] = await resp.json();
    return {
      skills: mergeCatalog(Array.isArray(data) ? data : []),
      error: false
    };
  } catch (err) {
    console.error("useSkillCatalog: failed to load skill catalog", err);
    return { skills: [], error: true };
  }
}

// Module-level cache shared across every mount of this hook within the page session. Added
// 2026-09-13 when the workflow-editor right-aside consumers (TransferToAIEditor.tsx,
// IntegrationSkillsSection.tsx) migrated off the static allSkillDefs.ts import: that right-aside
// remounts its AI node editor on every node the admin clicks, and without a cache each click
// refired a full GET SiteSkillOverrides/catalog round trip plus a visible "Loading skill
// catalog..." flash that briefly hid the Permitted Skills picker -- a real UX regression versus
// the old synchronous static import (flagged in Opus plan review, 2026-09-13). Only successful
// responses are cached; a failed fetch is never cached, so the very next mount gets a fresh real
// attempt instead of being stuck showing a stale error for the rest of the session.
//
// Site-scoping (Opus diff review MED-1, 2026-09-13): GetCatalog derives siteId from the caller's
// effective auth token, and a Velaro staff member can switch which site's token is effective
// (SupportSessionBanner.tsx's startSupportSession/clearSupportSession) WITHOUT a page reload --
// both fire a `window` "supportSessionChanged" event. A cache keyed only on "this page session"
// would serve site A's included/gating data to site B for up to CACHE_TTL_MS after a support-
// session switch. Every hook instance below subscribes to that event and forces a real refetch,
// and the module cache is invalidated the instant the event fires -- not just on the next
// scheduled expiry.
const CACHE_TTL_MS = 60_000;
let cache: { skills: CatalogSkill[]; fetchedAt: number } | null = null;
let inFlight: Promise<{ skills: CatalogSkill[]; error: boolean }> | null = null;
// Bumped on every invalidateSkillCatalogCache() call. A load() started before an
// invalidation must not be allowed to write its (now-stale) result back into `cache` after
// the invalidation happens -- otherwise a pre-switch fetch for site A that resolves AFTER a
// support-session switch to site B would silently repopulate the cache with site A's data,
// re-opening the exact leak the invalidation was meant to close (Opus diff review MED-A,
// 2026-09-13, a second-round finding on top of MED-1).
let cacheGeneration = 0;

function getFreshCache(): { skills: CatalogSkill[] } | null {
  if (!cache) return null;
  if (Date.now() - cache.fetchedAt > CACHE_TTL_MS) return null;
  return { skills: cache.skills };
}

/** Invalidates the module-level cache immediately -- call after any write that can change what
 *  `included` (or the skill set) resolves to for the CURRENT site (e.g. SkillControlsPanel.tsx's
 *  SiteSkillOverrides/BotSkillOverrides PUT), and internally on a support-session site switch.
 *  Does not force already-mounted consumers to re-render; call a hook's own `refetch()` for that. */
export function invalidateSkillCatalogCache(): void {
  cache = null;
  inFlight = null;
  cacheGeneration++;
}

/** Test-only alias -- clears the module-level cache/in-flight state so each test file/case starts
 *  from a clean slate instead of leaking a previous test's cached catalog into the next. */
export function __resetSkillCatalogCacheForTests(): void {
  invalidateSkillCatalogCache();
}

/**
 * Fetches the full live skill catalog (GET SiteSkillOverrides/catalog, unfiltered -- no
 * ?integration= param, see SkillControlsPanel.tsx's loadCatalog comment for why: the
 * server-side `integration` filter compares against IWorkflowSkill.Category, which doesn't
 * 1:1 match every integration page's own slug) and merges it with allSkillDefs.ts's
 * decoration-only UI copy. See module doc comment above for the merge contract.
 *
 * Successful results are cached at module scope for CACHE_TTL_MS -- see the cache comment above
 * for why, and for the site-scoping invalidation on a support-session switch. `refetch()` always
 * forces a real network call, bypassing (and then repopulating) the cache.
 */
export default function useSkillCatalog(): {
  skills: CatalogSkill[];
  loading: boolean;
  error: boolean;
  refetch: () => void;
} {
  const api = useApi();
  const [skills, setSkills] = useState<CatalogSkill[]>(
    () => getFreshCache()?.skills ?? []
  );
  const [loading, setLoading] = useState(() => getFreshCache() === null);
  const [error, setError] = useState(false);
  const [nonce, setNonce] = useState(0);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  // Force a real refetch the instant a Velaro staff member switches which site's token is
  // effective (see module comment above) -- a stale cache must not survive that switch even
  // within CACHE_TTL_MS.
  useEffect(() => {
    function handleSessionChange() {
      invalidateSkillCatalogCache();
      setNonce((n) => n + 1);
    }
    window.addEventListener("supportSessionChanged", handleSessionChange);
    return () =>
      window.removeEventListener("supportSessionChanged", handleSessionChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const forceRefetch = nonce > 0;

    if (!forceRefetch) {
      const fresh = getFreshCache();
      if (fresh) {
        setSkills(fresh.skills);
        setError(false);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    setError(false);

    async function load() {
      const gen = cacheGeneration; // captured before awaiting -- see cacheGeneration comment above
      // Hoisted so the `finally` below can check it's still the CURRENT in-flight promise --
      // a stale load resuming late must not null out a newer, still-in-progress request's
      // `inFlight` reference (Opus diff review, LOW finding, 2026-09-13).
      let promise!: Promise<{ skills: CatalogSkill[]; error: boolean }>;
      try {
        // A forced refetch always starts its own real request -- reusing a stale in-flight
        // promise here would silently defeat "refetch() always issues a real second GET."
        promise = forceRefetch
          ? (inFlight = fetchCatalog(api))
          : (inFlight ?? (inFlight = fetchCatalog(api)));
        const result = await promise;
        if (!cancelled) {
          setSkills(result.skills);
          setError(result.error);
        }
        if (!result.error && gen === cacheGeneration) {
          cache = { skills: result.skills, fetchedAt: Date.now() };
        }
      } finally {
        if (inFlight === promise) inFlight = null;
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce]);

  return { skills, loading, error, refetch };
}
